import angular from "angular"

import { StudentHelperService } from "../../services/StudentHelperService"
import AssignmentService from "../../../../app/services/assignment/assignment.service"
import { AssignmentExportService } from "../../services/AssignmentExportService"
import MediaService from "../../../../app/services/media/media.service"
import ClassCodeService from "../../../../app/services/class-code/class-code.service"
import { StudentHelperAssignmentPreparationStatus } from "../../models/StudentHelperAssignmentPreparationStatus"
import { ElementIntents } from "../../../../app/model/domain/element-metadata"
import { AssignmentSheetMetadata } from "../../../../app/components/assignment-sheet/assignment-sheet.directive"

export class StudentHelperTeacherController {
  public assignmentId!: string
  public rosterId?: string
  public isRosterAssigned = false
  public currentConfig?: AssignmentSheetMetadata

  constructor(
    private $scope: angular.IScope,
    private StudentHelperService: StudentHelperService,
    private AssignmentService: AssignmentService,
    private AssignmentExportService: AssignmentExportService,
    private MediaService: MediaService,
    private ClassCodeService: ClassCodeService,
    $stateParams: angular.ui.IStateParamsService,
  ) {
    "ngInject"

    this.assignmentId = $stateParams.assignmentId
    this.rosterId = $stateParams.rosterId
    this.isRosterAssigned = !!$stateParams.rosterId
  }

  async getPreparationStatus(): Promise<StudentHelperAssignmentPreparationStatus> {
    try {
      const initialStatus =
        await this.StudentHelperService.getPreparationTaskStatusForAssignment(
          this.assignmentId,
        )

      if (
        initialStatus.status === "processing" &&
        initialStatus.preparationTaskId
      ) {
        return this.StudentHelperService.pollPreparationTaskStatus(
          this.assignmentId,
          initialStatus.preparationTaskId,
        )
      }

      return initialStatus
    } catch (error) {
      console.error("Failed to get preparation status", error)
      if (error instanceof Error) throw error
      throw new Error("Failed to get preparation status: Unknown error")
    }
  }

  async startPreparation(): Promise<StudentHelperAssignmentPreparationStatus> {
    try {
      const assignment = await this.AssignmentService.get(this.assignmentId)

      const questionBlobMap =
        await this.AssignmentExportService.convertAssignmentQuestionsToBlobs(
          assignment,
          (config: AssignmentSheetMetadata) => {
            this.currentConfig = config
            this.$scope.$apply()
          },
        )

      await Promise.all(
        questionBlobMap.map(async ({ question, blob }) => {
          const mediaLink = await this.MediaService.saveQuestionThumbnail(
            question.id,
            blob,
            ElementIntents.CONTENT,
            question.elementListId,
            question.elementListDb,
          )
          question.elements.metadata = question.elements.metadata ?? {}
          question.elements.metadata["thumbnail_image_url"] = mediaLink
          return {
            question,
            mediaLink,
          }
        }),
      )

      const initialResponse = await this.StudentHelperService.prepareAssignment(
        this.assignmentId,
        { forced: false },
      )

      if (
        initialResponse.status === "processing" &&
        initialResponse.preparationTaskId
      ) {
        const finalStatus =
          await this.StudentHelperService.pollPreparationTaskStatus(
            this.assignmentId,
            initialResponse.preparationTaskId,
          )

        if (this.rosterId && finalStatus.status === "completed") {
          await this.setAllowInstantHelper(true)
        }
        return finalStatus
      }

      return initialResponse
    } catch (error) {
      if (this.rosterId) {
        await this.setAllowInstantHelper(false).catch((e) =>
          console.error("Failed to revert helper status", e),
        )
      }

      console.error("Failed to start preparation", error)
      if (error instanceof Error) throw error
      throw new Error("Failed to start preparation: Unknown error")
    }
  }

  async restartPreparation(): Promise<StudentHelperAssignmentPreparationStatus> {
    try {
      const assignment = await this.AssignmentService.get(this.assignmentId)

      const questionBlobMap =
        await this.AssignmentExportService.convertAssignmentQuestionsToBlobs(
          assignment,
          (config: AssignmentSheetMetadata) => {
            this.currentConfig = config
            this.$scope.$apply()
          },
        )

      await Promise.all(
        questionBlobMap.map(async ({ question, blob }) => {
          const mediaLink = await this.MediaService.saveQuestionThumbnail(
            question.id,
            blob,
            ElementIntents.CONTENT,
            question.elementListId,
            question.elementListDb,
          )
          question.elements.metadata = question.elements.metadata ?? {}
          question.elements.metadata["thumbnail_image_url"] = mediaLink
          return {
            question,
            mediaLink,
          }
        }),
      )

      const initialResponse = await this.StudentHelperService.prepareAssignment(
        this.assignmentId,
        { forced: true },
      )

      if (
        initialResponse.status === "processing" &&
        initialResponse.preparationTaskId
      ) {
        const finalStatus =
          await this.StudentHelperService.pollPreparationTaskStatus(
            this.assignmentId,
            initialResponse.preparationTaskId,
          )

        if (this.rosterId && finalStatus.status === "completed") {
          await this.setAllowInstantHelper(true)
        }
        return finalStatus
      }
      return initialResponse
    } catch (error) {
      if (this.rosterId) {
        await this.setAllowInstantHelper(false).catch((e) =>
          console.error("Failed to revert helper status", e),
        )
      }

      console.error("Failed to restart preparation", error)
      if (error instanceof Error) throw error
      throw new Error("Failed to restart preparation: Unknown error")
    }
  }

  /**
   * Fetches the initial state of allowInstantHelper for the current assignment and roster.
   */
  async getInitialAllowInstantHelper(): Promise<boolean> {
    if (!this.rosterId) {
      return false
    }
    const rosterSettings =
      await this.ClassCodeService.getForAssignmentAndRoster(
        this.assignmentId,
        this.rosterId,
      )
    return rosterSettings?.allowInstantHelper || false
  }

  /**
   * If a roster is assigned, it enables the Student Helper for that roster.
   */
  async setAllowInstantHelper(newValue: boolean): Promise<void> {
    if (!this.rosterId) {
      throw new Error("Cannot toggle Student Helper for an unassigned roster.")
    }

    await this.AssignmentService.addOrUpdateRoster(
      this.assignmentId,
      this.rosterId,
      false, // showStudentScores
      newValue, // allowInstantHelper
      false, // lockStudentWork
      false, // hideStudentWork
      true, // allowPdf
    )
  }
}
